import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useKeycloak } from '@react-keycloak/web';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { queryKeys } from 'shared/api/queryKeys';
import { addRouteDestinations, listRoutes } from 'routes/api';
import { Connection } from 'shared/types/ObjectTypes';
import { eligibleRoutes } from './routeCandidates';

type WorkspaceOption = { label: string; value: string };

// "Add to a Route": pick an existing Route and this record joins its
// destinations. Candidates come from the same Routes list the portal already
// uses, defaulting to the record's workspace.
export function useAddToRoute({
  connection,
  connectionId,
}: {
  connection: Connection;
  connectionId: string | undefined;
}) {
  const { keycloak } = useKeycloak();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [workspaceFilter, setWorkspaceFilter] =
    useState<WorkspaceOption | null>(null);
  const [search, setSearch] = useState('');

  const candidatesQuery = useQuery({
    queryKey: queryKeys.connections.routeCandidates(
      connectionId ?? '',
      search,
      workspaceFilter?.value ?? '',
    ),
    enabled: isOpen && !!keycloak.token && !keycloak.isTokenExpired(),
    queryFn: async () => {
      const data = await listRoutes(keycloak.token as string, {
        search,
        ordering: 'name',
      });
      return {
        eligible: eligibleRoutes(
          data.results,
          connectionId,
          workspaceFilter ? workspaceFilter.value : undefined,
        ),
        hasMore: !!data.next,
      };
    },
  });

  const open = () => {
    const ws = connection.owner?.id
      ? { label: connection.owner.name, value: connection.owner.id }
      : null;
    setWorkspaceFilter(ws);
    setSearch('');
    setIsOpen(true);
  };

  // `route` here is a route RECORD from the picker, so its id is the id to PATCH
  // and its destinations are its own. The URL to land on afterwards is a
  // different value: route pages are addressed by the provider INTEGRATION id.
  const attach = async (route: any) => {
    if (!keycloak.token) return;
    const route_id = route?.id;
    const routeUrlId = route?.data_providers?.[0]?.id;
    if (!route_id) {
      console.log('cannot attach: candidate has no route id', route);
      toast.error(
        t('Something went wrong. Please try again or contact support.'),
        { position: toast.POSITION.TOP_RIGHT },
      );
      return;
    }
    try {
      const existing = (route.destinations || []).map((d: any) => d.id);
      const updated = await addRouteDestinations(keycloak.token, route_id, {
        destinations: Array.from(
          new Set([...existing, connectionId]),
        ) as string[],
      });
      // The API layer swallows a 401 and resolves undefined; the login modal is
      // already up, so say nothing rather than claiming the record was attached.
      if (!updated) return;
      toast.success(t('Connection updated'), {
        position: toast.POSITION.TOP_RIGHT,
      });
      setIsOpen(false);
      // No provider means no Route page to land on — the toast above already
      // confirmed the attach, so stay put rather than navigate to a 404.
      // The panel to open travels in router state, not in the path.
      if (routeUrlId)
        navigate(`/routes/${routeUrlId}`, {
          state: {
            flowSelection: { kind: 'account', integrationId: connectionId },
          },
        });
    } catch (err: any) {
      console.log(err);
      if (err?.response?.status !== 401) {
        toast.error(
          t('Something went wrong. Please try again or contact support.'),
          { position: toast.POSITION.TOP_RIGHT },
        );
      }
    }
  };

  return {
    isOpen,
    open,
    close: () => setIsOpen(false),
    candidates: candidatesQuery.data?.eligible ?? [],
    loading: candidatesQuery.isFetching,
    hasMore: candidatesQuery.data?.hasMore ?? false,
    search: setSearch,
    workspaceFilter,
    setWorkspaceFilter,
    attach,
  };
}
